import React from 'react';
import { MessageCircle, Tag, TrendingDown, Package } from 'lucide-react';

function ComboCard({ combo, onClick }) {
  function handleWhatsApp(e) {
    e.stopPropagation();
    const phone = combo.seller.phone_number.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola! Estoy interesado en el combo: ${combo.name} (SKU: ${combo.sku})`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }

  function handleCardClick() {
    onClick(combo);
  }

  // Get up to 4 product images to display
  const displayImages = combo.item_images?.slice(0, 4) || [];
  const hasMoreItems = combo.total_items > displayImages.length;

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group border-2 border-transparent hover:border-brand-primary"
    >
      <div className="p-4 flex flex-col md:flex-row gap-4">
        {/* Left: Product Images Grid */}
        <div className="flex-shrink-0">
          <div className="grid grid-cols-2 gap-2 w-48 h-48">
            {displayImages.map((item, index) => (
              <div
                key={index}
                className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden"
              >
                <img
                  src={item.image_url}
                  alt={item.product_title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
                {item.quantity > 1 && (
                  <div className="absolute top-1 right-1 bg-brand-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full font-montserrat">
                    {item.quantity}x
                  </div>
                )}
              </div>
            ))}
            
            {/* Show placeholder for empty slots */}
            {[...Array(Math.max(0, 4 - displayImages.length))].map((_, index) => (
              <div
                key={`empty-${index}`}
                className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center"
              >
                <Package className="w-8 h-8 text-slate-300" />
              </div>
            ))}
          </div>

          {hasMoreItems && (
            <p className="text-xs text-slate-500 font-montserrat text-center mt-2">
              +{combo.total_items - displayImages.length} productos más
            </p>
          )}
        </div>

        {/* Right: Combo Information */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="mb-3">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-xl font-bold text-slate-800 font-montserrat leading-tight flex-1">
                {combo.name}
              </h3>
              
              {/* Discount Badge */}
              {combo.discount_percentage > 0 && (
                <div className="flex-shrink-0 ml-2">
                  <div className="bg-red-500 text-white px-2.5 py-1 rounded-full flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold font-montserrat">
                      -{combo.discount_percentage}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Provider */}
            <div className="flex items-center gap-2 text-sm text-slate-600 font-montserrat">
              <div className="w-2 h-2 bg-brand-primary rounded-full" />
              <span>{combo.seller.username}</span>
            </div>
          </div>

          {/* Description */}
          {combo.description && (
            <p className="text-sm text-slate-600 font-montserrat mb-3 line-clamp-2 leading-relaxed">
              {combo.description}
            </p>
          )}

          {/* Combo Stats */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-xs text-slate-500 font-montserrat mb-0.5">Productos incluidos</p>
              <p className="text-base font-bold text-slate-800 font-montserrat">
                {combo.total_items} items
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <p className="text-xs text-green-700 font-montserrat mb-0.5">Ahorras</p>
              <p className="text-base font-bold text-green-600 font-montserrat">
                S/ {combo.savings.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Pricing */}
          <div className="flex items-end justify-between mt-auto pt-3 border-t border-slate-200">
            <div>
              <p className="text-xs text-slate-500 font-montserrat line-through">
                S/ {combo.regular_price.toFixed(2)}
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-brand-primary font-montserrat">
                  S/ {combo.combo_price.toFixed(2)}
                </p>
                <Tag className="w-4 h-4 text-brand-gold mb-1" />
              </div>
            </div>

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg transition-all font-montserrat text-sm font-semibold shadow-md hover:shadow-lg"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Consultar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComboCard;